import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import sanityClient from "../../sanityClient";
import LazyLoad from "react-lazyload";
import tempImage from "../../assets/images/temp.jpg";

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);

  useEffect(() => {
    // Fetch project data from Sanity
    sanityClient
      .fetch(
        `*[_type == "build" && _id == $id][0] {
          name,
          description,
          "imageUrl": mainImage.asset->url,
          address,
          bedrooms,
          bathrooms,
          squareFootage,
          completionDate,
          startDate,
          propertyType,
          sponsors[]->{
            name,
            website,
            "logoUrl": logo.asset->url,
            "galleryImages": gallery[].asset->url,
            videos[] {
              title,
              description,
              url
            }
          },
          gallery[] {
            "imageUrl": asset->url,
            alt
          }
        }`,
        { id }
      )
      .then((data) => setProject(data))
      .catch(console.error);
  }, [id]);

  if (!project) return <Loading>Loading...</Loading>;

  // Dynamic opening statement
  const openingStatement = () => {
    if (project.sponsors && project.sponsors.length > 0) {
      return `${project.sponsors[0].name} is proud to sponsor this Habitat build located at ${project.address}. This project is part of their commitment to providing affordable housing in the community.`;
    } else {
      return `This project is an important part of Habitat's mission to provide affordable housing at ${project.address}.`;
    }
  };

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    project.address
  )}&output=embed`;

  return (
    <Container>
      <Title>{project.name}</Title>

      {/* Dynamic Opening Statement */}
      <DynamicOpeningStatement>{openingStatement()}</DynamicOpeningStatement>

      {/* Project Main Image */}
      <LazyLoad height={200} offset={100}>
        <Image src={project.imageUrl || tempImage} alt={project.name} />
      </LazyLoad>

      {/* Project Description */}
      <Description>{project.description}</Description>

      {/* Project Details */}
      <Details>
        <DetailItem>
          <strong>Address:</strong> {project.address}
        </DetailItem>
        {project.propertyType && (
          <DetailItem>
            <strong>Type:</strong> {project.propertyType}
          </DetailItem>
        )}
        {project.bedrooms && (
          <DetailItem>
            <strong>Bedrooms:</strong> {project.bedrooms}
          </DetailItem>
        )}
        {project.bathrooms && (
          <DetailItem>
            <strong>Bathrooms:</strong> {project.bathrooms}
          </DetailItem>
        )}
        {project.squareFootage && (
          <DetailItem>
            <strong>Square Footage:</strong> {project.squareFootage}
          </DetailItem>
        )}
        {project.startDate && (
          <DetailItem>
            <strong>Start Date:</strong>{" "}
            {new Date(project.startDate).toLocaleDateString()}
          </DetailItem>
        )}
        {project.completionDate && (
          <DetailItem>
            <strong>Completion Date:</strong>{" "}
            {new Date(project.completionDate).toLocaleDateString()}
          </DetailItem>
        )}
      </Details>

      {/* Map */}
      <MapContainer>
        <iframe
          width="100%"
          height="450"
          frameBorder="0"
          style={{ border: 0 }}
          src={mapSrc}
          allowFullScreen
          title="Google Map"
        ></iframe>
      </MapContainer>

      {/* Sponsors Section */}
      {project.sponsors && project.sponsors.length > 0 ? (
        <Section>
          <SectionTitle>Sponsors</SectionTitle>
          {project.sponsors.map((sponsor, index) => (
            <SponsorCard key={index}>
              {/* Sponsor Logo or Name as fallback */}
              {sponsor.logoUrl ? (
                <LazyLoad height={200} offset={100}>
                  <SponsorLogo src={sponsor.logoUrl} alt={sponsor.name} />
                </LazyLoad>
              ) : (
                <SponsorName>{sponsor.name}</SponsorName>
              )}

              {/* Dynamic Opening Statement */}
              <DynamicOpeningStatement>
                {sponsor.name} is proud to sponsor this Habitat build located at{" "}
                {project.address}. This project is part of their commitment to
                providing affordable housing in the community.
              </DynamicOpeningStatement>

              {/* Sponsor Website */}
              {sponsor.website && (
                <SponsorWebsite
                  href={sponsor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Website
                </SponsorWebsite>
              )}
            </SponsorCard>
          ))}
        </Section>
      ) : (
        <Section>
          <SectionTitle>Sponsors</SectionTitle>
          <p>
            There is no sponsor for this house yet. House sponsorships come with
            perks like designated build days, public signage, groundbreaking
            ceremonies, and much more.{" "}
            <Link to="/get-involved/sponsorship-packages">
              Learn more about House Sponsorships
            </Link>
            .
          </p>
          <ActionButton href="/get-involved/sponsorship-packages">
            Sponsor This House
          </ActionButton>
        </Section>
      )}

      {/* Photo Gallery */}
      {project.gallery && project.gallery.length > 0 ? (
        <Section>
          <SectionTitle>Photo Gallery</SectionTitle>
          <Gallery>
            {project.gallery.map((image, index) => (
              <LazyLoad key={index} height={100} offset={100}>
                <GalleryImage src={image.imageUrl} alt={image.alt} />
              </LazyLoad>
            ))}
          </Gallery>
        </Section>
      ) : project.sponsors &&
        project.sponsors.some(
          (sponsor) => sponsor.galleryImages && sponsor.galleryImages.length > 0
        ) ? (
        <Section>
          <SectionTitle>Photo Gallery</SectionTitle>
          <Gallery>
            {project.sponsors.map((sponsor) =>
              sponsor.galleryImages.map((imageUrl, index) => (
                <LazyLoad key={index} height={100} offset={100}>
                  <GalleryImage src={imageUrl} alt={`Gallery ${index + 1}`} />
                </LazyLoad>
              ))
            )}
          </Gallery>
        </Section>
      ) : (
        <Section>
          <SectionTitle>Photo Gallery</SectionTitle>
          <p>
            There are no photos at this time. Visit our{" "}
            <a
              href="https://www.facebook.com/habitatbuffalo"
              target="_blank"
              rel="noopener noreferrer"
            >
              Facebook
            </a>{" "}
            for the most recent photos and videos of our volunteers and team in
            action.
          </p>
        </Section>
      )}

      {/* Sponsor Videos */}
      {project.sponsors &&
        project.sponsors.some((sponsor) => sponsor.videos?.length > 0) && (
          <Section>
            <SectionTitle>Related Videos</SectionTitle>
            {project.sponsors.map(
              (sponsor) =>
                sponsor.videos &&
                sponsor.videos.length > 0 &&
                sponsor.videos.map((video, idx) => (
                  <VideoContainer key={idx}>
                    <iframe
                      src={video.url}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                    <VideoTitle>{video.title}</VideoTitle>
                    <VideoDescription>{video.description}</VideoDescription>
                  </VideoContainer>
                ))
            )}
          </Section>
        )}

      {/* Impact Statement */}
      <Section>
        <SectionTitle>Impact Statement</SectionTitle>
        <p>
          This project aims to provide affordable housing to families in need,
          fostering community development and stability. By partnering with
          volunteers and sponsors, we strive to make a lasting impact on our
          community. If you are interested in becoming a Habitat homeowner,{" "}
          <Link to="/how-to-apply">learn more</Link>.
        </p>
      </Section>

      {/* Contact Us */}
      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <p>
          For more information about this project, please contact us. Visit our{" "}
          <Link to="/contact">contact page</Link> for more details.
        </p>
      </Section>
    </Container>
  );
};

// Styled Components

const Loading = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 1.5em;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  text-align: left;
  background: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const Title = styled.h1`
  font-size: 2.5em;
  color: var(--hhb-primary);
  text-align: center;
  margin-bottom: 20px;
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const Description = styled.p`
  font-size: 1.2em;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const Details = styled.ul`
  list-style: none;
  padding: 0;
`;

const DetailItem = styled.li`
  font-size: 1em;
  margin-bottom: 10px;
  line-height: 1.5;

  strong {
    color: var(--hhb-darkblue);
  }
`;

const MapContainer = styled.div`
  margin-top: 20px;
`;

// Sponsors Section Styled Components

const SponsorCard = styled.div`
  margin-bottom: 40px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const SponsorLogo = styled.img`
  max-width: 350px;
  width: 100%;
  height: auto;
  margin: 0 auto 20px;
  display: block;
`;

const SponsorName = styled.h3`
  font-size: 2em;
  text-align: center;
`;

const SponsorWebsite = styled.a`
  color: var(--hhb-primary);
  text-decoration: none;
  font-size: 1.2em;
  display: block;
  text-align: center;
  margin-bottom: 20px;

  &:hover {
    text-decoration: underline;
  }
`;

const SponsorGallery = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

const GalleryImage = styled.img`
  width: calc(33.333% - 10px);
  height: auto;
  border-radius: 8px;
`;

const Section = styled.div`
  margin-top: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 2em;
  color: var(--hhb-primary);
  margin-bottom: 20px;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    object-fit: cover;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const VideoContainer = styled.div`
  margin-bottom: 20px;

  iframe {
    width: 100%;
    height: 400px;
    border-radius: 8px;
  }
`;

const VideoTitle = styled.h4`
  font-size: 1.2em;
  margin-top: 10px;
`;

const VideoDescription = styled.p`
  font-size: 1em;
  margin-top: 5px;
  line-height: 1.5;
`;

const ActionButton = styled.a`
  background-color: var(--hhb-primary);
  color: var(--hhb-white);
  padding: 10px 20px;
  text-decoration: none;
  border-radius: 5px;
  font-size: 1em;
  font-family: var(--font-title);
  text-align: center;
  display: block;
  margin: 20px auto;
  max-width: 200px;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--hhb-darkblue);
  }
`;

const DynamicOpeningStatement = styled.p`
  font-size: 1.3em;
  margin-bottom: 20px;
  text-align: center;
`;

export default ProjectDetail;
